import React, { useState, useEffect } from "react";
import "./users.css";
import {
  Table,
  Avatar,
  Input,
  Badge,
  message,
  ConfigProvider,
  Button,
  Tag,
  Modal,
  Space,
  Image,
  Typography,
  Segmented,
  Select,
} from "antd";
import {
  SearchOutlined,
  UserOutlined,
  FileSearchOutlined,
} from "@ant-design/icons";
import { callApi } from "../../api/apiCaller";
import { routes } from "../../api/routes";
import Loader from "../../components/loader/loader";
import {
  listFromResponse,
  isMutationSuccess,
  getSellerApprovalStatus,
  sortUsersBySellerStatus,
} from "../../helper/helper";
import dayjs from "dayjs";

const { Text } = Typography;

const isPdfUrl = (url) =>
  typeof url === "string" && /\.pdf(\?|$)/i.test(url.trim());

const DocumentPreview = ({ label, url }) => {
  if (!url) {
    return <Text type="secondary">Not provided</Text>;
  }
  if (isPdfUrl(url)) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className="seller-doc-open-link">
        Open PDF in new tab
      </a>
    );
  }
  return (
    <Image
      src={url}
      alt={label}
      className="seller-doc-image"
      style={{ maxWidth: "100%", maxHeight: 280, objectFit: "contain" }}
    />
  );
};

const SellerStatusBadge = ({ user }) => {
  const status = getSellerApprovalStatus(user);
  if (status === "pending_review") {
    return <Tag color="warning">Pending review</Tag>;
  }
  if (status === "approved") {
    return <Tag color="success">Approved</Tag>;
  }
  return <Tag color="default">Not a seller</Tag>;
};

const filterCustomers = (source, search, sellerFilter) => {
  let list = sortUsersBySellerStatus(source);
  if (sellerFilter === "pending_review") {
    list = list.filter((u) => getSellerApprovalStatus(u) === "pending_review");
  } else if (sellerFilter === "approved") {
    list = list.filter((u) => getSellerApprovalStatus(u) === "approved");
  }
  if (search) {
    const lower = search.toLowerCase();
    list = list.filter((u) => (u.name || "").toLowerCase().includes(lower));
  }
  return list;
};

const Users = () => {
  const [customers, setCustomers] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sellerFilter, setSellerFilter] = useState("all");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [reviewUser, setReviewUser] = useState(null);

  const pendingCount = allCustomers.filter(
    (u) => getSellerApprovalStatus(u) === "pending_review"
  ).length;

  useEffect(() => {
    getCustomers();
  }, []);

  useEffect(() => {
    const filtered = filterCustomers(allCustomers, searchText, sellerFilter);
    setCustomers(filtered);
    setPagination((prev) => ({
      ...prev,
      current: 1,
      total: filtered.length,
    }));
  }, [sellerFilter, allCustomers, searchText]);

  const getCustomers = () => {
    const params = new URLSearchParams({
      page: "1",
      limit: "500",
    });

    callApi(
      "GET",
      `${routes.getAllCustomers}?${params.toString()}`,
      null,
      setIsLoading,
      (res) => {
        if (res.status === 200) {
          setAllCustomers(listFromResponse(res));
        }
      },
      () => message.error("Failed to load customers")
    );
  };

  const handleSearch = (value) => {
    setSearchText(value || "");
  };

  const handleTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current,
      pageSize: newPagination.pageSize,
    }));
  };

  const handleAccountTypeChange = (userId, accountType) => {
    callApi(
      "PATCH",
      `${routes.updateUser}/${userId}`,
      { accountType },
      setIsLoading,
      (res) => {
        if (isMutationSuccess(res)) {
          message.success("Account type updated");
          setAllCustomers((prev) =>
            prev.map((u) => (u._id === userId ? { ...u, accountType } : u))
          );
        }
      },
      () => message.error("Failed to update account type")
    );
  };

  const handleApproveSeller = (userId) => {
    callApi(
      "PATCH",
      `${routes.approveSeller}/${userId}`,
      {},
      setIsLoading,
      (res) => {
        if (isMutationSuccess(res)) {
          message.success("Seller approved");
          getCustomers();
        }
      },
      (err) => {
        console.error("[Users] approve-seller error", err);
        message.error("Failed to approve seller");
      }
    );
  };

  const columns = [
    {
      title: "No",
      key: "index",
      width: 60,
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "Customer Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div className="customer-name-cell">
          <Avatar
            src={record.image}
            icon={<UserOutlined />}
            size={32}
            style={{ backgroundColor: "#555BDF" }}
          />
          <span className="customer-name">{text || "N/A"}</span>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (email) => email || "N/A",
    },
    {
      title: "Phone Number",
      dataIndex: "number",
      key: "number",
      render: (phone) => phone || "N/A",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      render: (role) => (
        <Badge
          status={role === "admin" ? "success" : "default"}
          text={role || "user"}
        />
      ),
    },
    {
      title: "Account type",
      dataIndex: "accountType",
      key: "accountType",
      width: 150,
      render: (accountType, r) =>
        r.role === "admin" ? (
          "—"
        ) : (
          <Select
            size="small"
            value={accountType || "personal"}
            style={{ width: 120 }}
            onChange={(value) => handleAccountTypeChange(r._id, value)}
            options={[
              { value: "personal", label: "Personal" },
              { value: "business", label: "Business" },
            ]}
          />
        ),
    },
    {
      title: "Joined Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => (date ? dayjs(date).format("DD MMM YYYY") : "N/A"),
    },
    {
      title: "Seller status",
      key: "sellerApproval",
      width: 140,
      render: (_, r) => <SellerStatusBadge user={r} />,
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      fixed: "right",
      render: (_, r) => {
        const status = getSellerApprovalStatus(r);
        if (status === "none") {
          return <span className="customers-actions-placeholder">—</span>;
        }
        return (
          <Button
            type={status === "pending_review" ? "primary" : "default"}
            size="small"
            icon={<FileSearchOutlined />}
            onClick={() => setReviewUser(r)}
          >
            Review documents
          </Button>
        );
      },
    },
  ];

  return (
    <ConfigProvider theme={{ token: { colorPrimary: "#555BDF" } }}>
      <div className="customers-container">
        <Loader loading={isLoading} />
        <div className="customers-header">
          <h1 className="customers-title">Customers</h1>
          {pendingCount > 0 && (
            <Tag color="warning" className="customers-pending-banner">
              {pendingCount} seller{pendingCount === 1 ? "" : "s"} pending review
            </Tag>
          )}
        </div>

        <div className="customers-card">
          <div className="customers-card-header">
            <h2 className="customers-card-title">Customers Details</h2>
            <Space wrap className="customers-toolbar">
              <Segmented
                value={sellerFilter}
                onChange={setSellerFilter}
                options={[
                  { label: "All", value: "all" },
                  { label: "Pending review", value: "pending_review" },
                  { label: "Approved sellers", value: "approved" },
                ]}
              />
              <Input
                placeholder="Search by name..."
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 260 }}
              />
            </Space>
          </div>

          <Modal
            title={
              reviewUser
                ? `Seller documents — ${reviewUser.name || "Customer"}`
                : ""
            }
            open={!!reviewUser}
            onCancel={() => setReviewUser(null)}
            width={720}
            destroyOnClose
            className="seller-doc-modal"
            footer={
              reviewUser ? (
                <Space>
                  <Button onClick={() => setReviewUser(null)}>Close</Button>
                  {getSellerApprovalStatus(reviewUser) === "pending_review" ? (
                    <Button
                      type="primary"
                      onClick={() => {
                        const id = reviewUser._id;
                        setReviewUser(null);
                        handleApproveSeller(id);
                      }}
                    >
                      Approve seller
                    </Button>
                  ) : (
                    <Tag color="success">Seller already approved</Tag>
                  )}
                </Space>
              ) : null
            }
          >
            {reviewUser && (
              <div className="seller-doc-review">
                <div className="seller-doc-meta">
                  <p>
                    <Text strong>Document title:</Text>{" "}
                    {reviewUser.documentTitle || "—"}
                  </p>
                  <p>
                    <Text strong>Date of birth:</Text> {reviewUser.dob || "—"}
                  </p>
                  <p>
                    <Text strong>Email:</Text> {reviewUser.email || "—"}
                  </p>
                  <p>
                    <Text strong>Phone:</Text> {reviewUser.number || "—"}
                  </p>
                  <p>
                    <Text strong>Status:</Text>{" "}
                    <SellerStatusBadge user={reviewUser} />
                  </p>
                </div>
                <div className="seller-doc-grid">
                  <div className="seller-doc-panel">
                    <Text strong className="seller-doc-panel-title">
                      Front
                    </Text>
                    <DocumentPreview
                      label="Front"
                      url={reviewUser.documentUrlFront}
                    />
                  </div>
                  <div className="seller-doc-panel">
                    <Text strong className="seller-doc-panel-title">
                      Back
                    </Text>
                    <DocumentPreview
                      label="Back"
                      url={reviewUser.documentUrlBack}
                    />
                  </div>
                </div>
                <Text type="secondary" className="seller-doc-footnote">
                  Review the documents above, then approve if the trade license is valid.
                </Text>
              </div>
            )}
          </Modal>

          <Table
            columns={columns}
            dataSource={customers}
            loading={isLoading}
            rowKey="_id"
            rowClassName={(record) =>
              getSellerApprovalStatus(record) === "pending_review"
                ? "pending-seller-row"
                : ""
            }
            scroll={{ x: 1400, y: "calc(100vh - 400px)" }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} customers`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            onChange={handleTableChange}
            className="customers-table"
          />
        </div>
      </div>
    </ConfigProvider>
  );
};

export default Users;
